import { useState } from 'react';
import { message, Button as AntButton, Form } from 'antd';
import { backendPatchWithoutRefresh } from '../../../helpers/callBackend';
import { Button, TextField, Box, Typography } from '@mui/material';
import Stack from '@mui/material/Stack';
import LoadingButton from '@mui/lab/LoadingButton';
import TableWithLoadingWrapper from '../../../components/generic/dataGrid';
import GenericDropdown from '../../../components/generic/genericDropdown';
import GenericDeleteModal from '../../../components/buttons/genericDeleteModal';
import PermissionGate from '../../../helpers/PermissionGate';

const Purposes = (props) => {
  const [submitting, setSubmitting] = useState(false);
  const [editPurposeSubmittingMap, setEditPurposeSubmittingMap] = useState({});
  const [editedPurposeMap, setEditedPurposeMap] = useState(
    (props.purposeArr || []).reduce((acc, purpose) => {
      acc[purpose.id] = { name: purpose.name, description: purpose.description };
      return acc;
    }, {})
  );
  const [editMode, setEditMode] = useState({});

  const editPurpose = (purposeId) => {
    setEditPurposeSubmittingMap(prev => ({ ...prev, [purposeId]: true }));
    backendPatchWithoutRefresh("/api/bank-account/purpose/", {
      purpose_id: purposeId,
      purpose_name: editedPurposeMap[purposeId].name,
      purpose_description: editedPurposeMap[purposeId].description
    }).then((res) => {
      setEditPurposeSubmittingMap(prev => ({ ...prev, [purposeId]: false }));
      if (res.status === 200) {
        props.onSuccess(prev => prev.map(p => p.id === purposeId ? {
          ...p,
          name: editedPurposeMap[purposeId].name,
          description: editedPurposeMap[purposeId].description
        } : p));
        setEditMode(prev => ({ ...prev, [purposeId]: false }));
        message.success('Purpose updated successfully');
      }
    })
      .catch(() => {
        setEditPurposeSubmittingMap(prev => ({ ...prev, [purposeId]: false }));
        message.error('Failed to update purpose');
      });
  };

  const updateEditedPurpose = (id, key, value) => {
    setEditedPurposeMap(prev => ({
      ...prev,
      [id]: {
        ...(prev[id] || {}),
        [key]: value
      }
    }));
  };


  const handleEditMode = (purposeId, val) => {
    setEditMode(prev => ({ ...prev, [purposeId]: val }));
  };


  const columns = [
    {
      field: 'name',
      headerName: 'Purpose Name',
      flex: 1,
      display: 'flex',
      renderCell: (params) => (
        <Box width={"100%"}>
          {
            editMode[params.row.id] ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={editedPurposeMap[params.row.id]?.name || ''}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    updateEditedPurpose(params.row.id, 'name', newValue);
                  }}
                  onKeyDown={(event) => {
                    event.stopPropagation()
                  }}
                />
              </Stack>
            ) : (
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography>{params.row.name}</Typography>
              </Stack>
            )
          }
        </Box>
      )
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 2,
      display: 'flex',
      renderCell: (params) => (
        <Box width={"100%"}>
          {
            editMode[params.row.id] ? (
              <Stack direction="row" spacing={1}
                sx={{
                  justifyContent: "flex-end",
                  alignItems: "baseline",
                }}
              >
                <TextField
                  variant="outlined"
                  size="small"
                  autoFocus
                  fullWidth
                  value={editedPurposeMap[params.row.id]?.description || ''}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    updateEditedPurpose(params.row.id, 'description', newValue);
                  }}
                  onKeyDown={(event) => {
                    event.stopPropagation()
                  }}
                />
                <LoadingButton
                  onClick={() => editPurpose(params.row.id)}
                  loading={editPurposeSubmittingMap[params.row.id] || false}
                  disableElevation type="submit" variant="contained" color="primary"
                  sx={{ marginLeft: 2, fontSize: 14, textTransform: 'none' }}
                >
                  Save
                </LoadingButton>

                <Button
                  onClick={() => handleEditMode(params.row.id, false)}
                  disableElevation type="submit" variant="outlined" color="secondary"
                  sx={{ ml: 2, fontSize: 14, textTransform: 'none' }}
                >
                  Cancel
                </Button>
              </Stack>
            ) : (
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography>{params.row.description}</Typography>
              </Stack>
            )
          }
        </Box>
      )
    },
    {
      field: 'action',
      headerName: '',
      flex: 0.1,
      align: "right",
      sortable: false,
      renderCell: (params) => {

        return (
          <PermissionGate requiredRole='dashboardEditor'>
            <Box display="flex" justifyContent="center">
              <GenericDropdown
                dropdownItems={[
                  <span
                    type="text"
                    onClick={() => handleEditMode(params.row.id, true)}
                    style={{
                      cursor: "pointer",
                      color: "#1890ff",
                      width: '100%',
                      textAlign: 'left'
                    }}
                  >
                    Edit
                  </span>,
                  <GenericDeleteModal
                    type='Bank Account Purpose'
                    title={`Are you sure you want to delete ${params.row.name}`}
                    URL='/api/bank-account/purpose/'
                    deleteParams={{ purpose_id: params.row.id }}
                    description={`By clicking Delete below you will no longer have the purpose ${params.row.name} on your dashboard. If you do not want to proceed or unsure, please click X on the upper right.`}
                    handleDeleteSuccess={() => props.deletePurpose(params.row.id)}
                    handleDeleteFailure={() => message.error(`Failed to delete the purpose ${params.row.name}`)}
                  />
                ]}
                trigger={['click']}
                placement="bottomRight"
              />
            </Box>
          </PermissionGate>
        );
      }
    }
  ];

  return (
    <Box>
      <TableWithLoadingWrapper
        columns={columns}
        dataSource={props.purposeArr}
        loading={false}
        pagination={{
          paginationModel: { pageSize: props.purposeArr?.length || 10, page: 0 },
        }}
        pageSizeOptions={[10, 20, 30]}
        getRowId={(row) => row.id}
      />
    </Box>
  );
};

export default Purposes;
